<template>
	<el-dialog v-dialogDrag top="3vh" :close-on-click-modal="false" :close-on-press-escape="false"
		:visible.sync="isShow" width="96%" :before-close="handleClose">
		<template #title>
			<div class="rtc-group-video-title">
				<span>群聊视频通话</span>
				<span class="call-duration">{{ callDurationText }}</span>
			</div>
		</template>
		<div class='rtc-group-video'>
			<div class="video-container">
				<div class="video-grid" :class="gridLayoutClass">
					<!-- 远程视频 -->
					<div v-for="peer in peers" :key="peer.id" class="video-item">
						<video :ref="'remoteVideo-' + peer.id" :id="'video-' + peer.id" autoplay playsinline
							@loadedmetadata="onVideoLoaded(peer.id, 'remote')" 
							@error="onVideoError(peer.id, 'remote')"
							@canplay="onVideoCanPlay(peer.id, 'remote')"
							@playing="onVideoPlaying(peer.id, 'remote')"></video>
						<div class="video-info">
							<span class="peer-name">{{ peer.nickName }}</span>
							<span v-if="peer.isCamera" class="camera-status">📹</span>
							<span v-if="peer.isMicroPhone" class="mic-status">🎤</span>
						</div>
						<!-- 最大化按钮 -->
						<div class="maximize-btn" @click="maximizeVideo(peer.id, 'remote')" title="最大化">
							<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
								<path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
							</svg>
						</div>
					</div>
					<!-- 本地视频 -->
					<div class="video-item local-video">
						<video ref="localVideo" autoplay muted playsinline
							@loadedmetadata="onVideoLoaded('local', 'local')" 
							@error="onVideoError('local', 'local')"></video>
						<div class="video-info">
							<span class="peer-name">{{ userStore.userInfo.nickName }} (我)</span>
							<span v-if="isCamera" class="camera-status">📹</span>
							<span v-if="isMicroPhone" class="mic-status">🎤</span>
						</div>
						<!-- 最大化按钮 -->
						<div class="maximize-btn" @click="maximizeVideo('local', 'local')" title="最大化">
							<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
								<path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
							</svg>
						</div>
					</div>
					<!-- 离开的用户 -->
					<div v-for="leftUser in leftUsers" :key="'left-' + leftUser.id" class="video-item left-user">
						<div class="left-user-placeholder">
							<div class="left-user-avatar">
								<head-image :name="leftUser.nickName" :url="leftUser.headImage || ''" :size="40"
									radius="50%" :isShowUserInfo="false"></head-image>
							</div>
							<div class="left-user-info">
								<div class="left-user-name">{{ leftUser.nickName }}</div>
								<div class="left-user-status">已离开</div>
							</div>
						</div>
					</div>
					
					<!-- 拒绝的用户 -->
					<div v-for="rejectedUser in rejectedUsers" :key="'rejected-' + rejectedUser.id" class="video-item rejected-user">
						<div class="rejected-user-placeholder">
							<div class="rejected-user-avatar">
								<head-image :name="rejectedUser.nickName" :url="rejectedUser.headImage || ''" :size="40"
									radius="50%" :isShowUserInfo="false"></head-image>
							</div>
							<div class="rejected-user-info">
								<div class="rejected-user-name">{{ rejectedUser.nickName }}</div>
								<div class="rejected-user-status">已拒绝</div>
							</div>
						</div>
					</div>
					
					<!-- 超时的用户 -->
					<div v-for="timeoutUser in timeoutUsers" :key="'timeout-' + timeoutUser.id" class="video-item timeout-user">
						<div class="timeout-user-placeholder">
							<div class="timeout-user-avatar">
								<head-image :name="timeoutUser.nickName" :url="timeoutUser.headImage || ''" :size="40"
									radius="50%" :isShowUserInfo="false"></head-image>
							</div>
							<div class="timeout-user-info">
								<div class="timeout-user-name">{{ timeoutUser.nickName }}</div>
								<div class="timeout-user-status">已超时</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			
			<div class="control-panel">
				<div class="control-buttons">
					<el-button @click="toggleCamera" :type="isCamera ? 'primary' : 'danger'" size="small">
						{{ isCamera ? '关闭摄像头' : '开启摄像头' }}
					</el-button>
					<el-button @click="toggleMicrophone" :type="isMicroPhone ? 'primary' : 'danger'" size="small">
						{{ isMicroPhone ? '关闭麦克风' : '开启麦克风' }}
					</el-button>
					<el-button @click="toggleScreenShare" :type="isScreenSharing ? 'warning' : 'primary'" size="small">
						{{ isScreenSharing ? '停止分享' : '屏幕分享' }}
					</el-button>
					<el-button @click="onInviteUser" type="success" size="small">邀请用户</el-button>
					<el-button @click="handleClose" type="danger" size="small">挂断</el-button>
				</div>
				<div class="status-info">
					<span>状态: {{ connectionStatus }}</span>
					<span>参与者: {{ peers.length + 1 }}人</span>
					<span v-if="leftUsers.length > 0">已离开: {{ leftUsers.length }}人</span>
					<span v-if="rejectedUsers.length > 0">已拒绝: {{ rejectedUsers.length }}人</span>
					<span v-if="timeoutUsers.length > 0">已超时: {{ timeoutUsers.length }}人</span>
				</div>
			</div>
		</div>
		
		<!-- 邀请用户选择器 -->
		<group-member-selector v-if="rtcInfo" ref="inviteSel" :group="{ id: rtcInfo.groupId }" title="邀请用户加入通话" @complete="onInviteComplete"></group-member-selector>

	</el-dialog>
</template>

<script>
import ImWebRtc from '@/api/webrtc';
import RtcGroupApi from '@/api/rtcGroupApi';
import { useGroupStore } from '@/store/groupStore'
import useUserStore from "@/store/userStore";
import GroupMemberSelector from '../group/GroupMemberSelector.vue';
import HeadImage from '../common/HeadImage.vue';


export default {
	name: "rtcGroupVideo",
	components: {
		GroupMemberSelector,
		HeadImage
	},
	computed: {
			userStore() {
				return useUserStore();
			},
			configuration() {
				const iceServers = this.configStore.webrtc.iceServers;
				return {
					iceServers: iceServers
				};
			},
			groupVideoMaxChannel() {
				const configuredMax = Number(this.configStore.webrtc.maxChannel);
				return Number.isFinite(configuredMax) && configuredMax > 1
					? Math.min(8, Math.floor(configuredMax))
					: 8;
			},
			inviteRemainingCapacity() {
				const occupiedIds = new Set([String(this.userStore.userInfo.id)]);
				this.peers.forEach(peer => {
					if (peer && peer.id !== undefined && peer.id !== null) {
						occupiedIds.add(String(peer.id));
					}
				});
				return Math.max(0, this.groupVideoMaxChannel - occupiedIds.size);
			},
			totalTiles() {
			return this.peers.length + 1 + this.leftUsers.length + this.rejectedUsers.length + this.timeoutUsers.length;
		},
		gridColumnCount() {
			if (this.totalTiles <= 1) {
				return 1;
			}
			if (this.totalTiles <= 4) {
				return this.totalTiles;
			}
			return 4;
		},
		gridLayoutClass() {
			return `grid-layout-${this.gridColumnCount}`;
		},
		callDurationText() {
			const minutes = Math.floor(this.callDuration / 60);
			const seconds = this.callDuration % 60;
			const minuteText = minutes < 10 ? `0${minutes}` : `${minutes}`;
			const secondText = seconds < 10 ? `0${seconds}` : `${seconds}`;
			return `${minuteText}:${secondText}`;
		}
	},
	data() {
		return {
			isShow: false,
			rtcInfo: null,
			localStream: null,
			peers: [],
			connectionStatus: '初始化中...',
			isCamera: true,
			isMicroPhone: true,
			isScreenSharing: false,
			screenStream: null,
			// 最大化相关
			isMaximized: false,
			maximizedVideo: null,
			maximizedVideoType: '',
			maximizedVideoId: '',
			// 为每个参与者创建独立的WebRTC实例
			webrtcInstances: {},
			// 为每个参与者存储候选信息
			candidates: {},
			// 远端描述未就绪时，先缓存远端候选信息
			pendingRemoteCandidates: {},
			// Web 接听前以及本地媒体尚未就绪时收到的协商消息
			pendingRtcMessages: [],
			// 标记哪些用户已经先向本端发过 Offer，避免接听后再次双向发 Offer
			receivedOfferUserIds: {},
			signalingReady: false,
			// 当前状态
			state: "CLOSE", // CLOSE:关闭 WAITING:等待 CHATING:聊天中
			// API实例
			API: new RtcGroupApi(),
			// 离开的用户列表
			leftUsers: [],
			// 新增：拒绝的用户列表
			rejectedUsers: [],
			// 新增：超时的用户列表
			timeoutUsers: [],
			// 新增：用于标记是否正在清理资源
			isCleaning: false,
			callDuration: 0,
			callDurationTimer: null,
			heartbeatTimer: null
		}
	},
		methods: {
			startCallTimer() {
				if (this.callDurationTimer) {
					return;
				}
			this.callDurationTimer = setInterval(() => {
				this.callDuration++;
			}, 1000);
		},
			stopCallTimer() {
				if (this.callDurationTimer) {
					clearInterval(this.callDurationTimer);
					this.callDurationTimer = null;
				}
			},
			startHeartbeat() {
				this.stopHeartbeat();
				if (!this.rtcInfo || !this.rtcInfo.groupId || !this.userStore.userInfo.id) {
					return;
				}
				this.API.heartbeat(this.rtcInfo.groupId, this.userStore.userInfo.id).catch(error => {
					this.handleHeartbeatError(error);
				});
				this.heartbeatTimer = setInterval(() => {
					this.API.heartbeat(this.rtcInfo.groupId, this.userStore.userInfo.id).catch(error => {
						this.handleHeartbeatError(error);
					});
				}, 10000);
			},
			handleHeartbeatError(error) {
				const message = this.extractErrorMessage(error, '');
				if (message.includes('群聊通话已结束') || message.includes('群聊通话已自动结束')) {
					console.log('后端确认群聊通话已结束，自动关闭Web通话页:', message);
					this.closeByRemoteCancel('群聊通话已自动结束');
					return;
				}
				console.warn('群聊心跳发送失败:', error);
			},
			stopHeartbeat() {
				if (this.heartbeatTimer) {
					clearInterval(this.heartbeatTimer);
					this.heartbeatTimer = null;
				}
			},
			extractErrorMessage(error, fallback = '群聊通话已结束') {
				if (!error) {
					return fallback;
				}
				if (typeof error === 'string') {
					return error;
				}
				if (error.message) {
					return error.message;
				}
				if (error.data && error.data.message) {
					return error.data.message;
				}
				return fallback;
			},
			closeByRemoteCancel(message = '群聊通话已取消') {
				this.cleanup();
				this.isShow = false;
				if (message) {
					this.$message.info(message);
				}
			},
			// 邀请用户
			onInviteUser() {
					if (!this.rtcInfo) return;
				if (this.inviteRemainingCapacity <= 0) {
					this.$message.warning(`群聊通话最多${this.groupVideoMaxChannel}人`);
					return;
				}
			// 收集已在通话中/已拒绝/已超时的用户ID
			const hideIds = [];
			// 自己
			hideIds.push(this.userStore.userInfo.id);
			// 正在通话的
			this.peers.forEach(p => { if (!hideIds.includes(p.id)) hideIds.push(p.id); });
			// 已拒绝的
			this.rejectedUsers.forEach(u => { if (!hideIds.includes(u.id)) hideIds.push(u.id); });
			// 已超时的
			this.timeoutUsers.forEach(u => { if (!hideIds.includes(u.id)) hideIds.push(u.id); });
			// 已离开的
			this.leftUsers.forEach(u => { if (!hideIds.includes(u.id)) hideIds.push(u.id); });
			console.log('邀请用户 - 排除ID列表:', hideIds);
				this.$refs.inviteSel.open(this.inviteRemainingCapacity, [], [], hideIds);
			},
		// 邀请完成回调
			onInviteComplete(selectedMembers) {
				if (!selectedMembers || selectedMembers.length === 0) return;
				if (selectedMembers.length > this.inviteRemainingCapacity) {
					this.$message.warning(`当前最多还能邀请${this.inviteRemainingCapacity}人`);
					return;
				}
			const newUsers = selectedMembers.map(m => ({
				id: m.userId,
				nickName: m.showNickName || m.nickName,
				headImage: m.headImage,
				isCamera: false,
				isMicroPhone: true
			}));
			// 调用后端发送群聊邀请
			this.API.invite(this.rtcInfo.groupId, newUsers).then(() => {
				console.log('邀请新成员成功');
				newUsers.forEach(u => {
					if (!this.peers.find(p => p.id === u.id)) {
						this.peers.push(u);
					}
				});
				this.$message.success('已发送邀请');
			}).catch(err => {
				console.error('邀请新成员失败:', err);
				this.$message.error(this.extractErrorMessage(err, '邀请失败'));
			});
		},
		// 所有人都先执行这个
		mergeActivePeers(userInfos = []) {
			const currentUserId = String(this.userStore.userInfo.id);
			const mergedPeers = [];
			userInfos.forEach(user => {
				if (!user || user.id === undefined || user.id === null || String(user.id) === currentUserId) {
					return;
				}
				const peerId = String(user.id);
				let peer = this.peers.find(item => item && String(item.id) === peerId);
				if (peer) {
					Object.keys(user).forEach(key => this.$set(peer, key, user[key]));
				} else {
					peer = {
						...user,
						nickName: user.nickName || user.showNickName || `用户${user.id}`,
						isCamera: !!user.isCamera,
						isMicroPhone: user.isMicroPhone !== false
					};
					this.peers.push(peer);
				}
				mergedPeers.push(peer);
			});
			return mergedPeers;
		},
		isNegotiationMessage(msg) {
			if (!msg) {
				return false;
			}
			return [
				this.$enums.MESSAGE_TYPE.RTC_GROUP_OFFER,
				this.$enums.MESSAGE_TYPE.RTC_GROUP_ANSWER,
				this.$enums.MESSAGE_TYPE.RTC_GROUP_CANDIDATE
			].includes(msg.type);
		},
		queueRtcMessage(msg) {
			if (msg) {
				this.pendingRtcMessages.push(msg);
				console.log('Web 群聊协商尚未就绪，暂存消息:', msg.type, msg.sendId);
			}
		},
		async ensurePeerFromMessage(msg) {
			const fromUserId = msg && msg.sendId;
			if (fromUserId === undefined || fromUserId === null) {
				return null;
			}
			let peer = this.peers.find(item => item && String(item.id) === String(fromUserId));
			if (!peer) {
				peer = this.mergeActivePeers([{
					id: fromUserId,
					nickName: msg.sendNickName || `用户${fromUserId}`,
					headImage: msg.sendHeadImage || '',
					isCamera: false,
					isMicroPhone: true
				}])[0];
				await this.$nextTick();
			}
			if (peer) {
				this.initPeerConnection(peer);
			}
			return peer;
		},
		parseRtcMessageContent(msg) {
			return typeof msg.content === 'string' ? JSON.parse(msg.content) : msg.content;
		},
		async processNegotiationMessage(msg) {
			const peer = await this.ensurePeerFromMessage(msg);
			if (!peer) {
				console.warn('忽略缺少发送者信息的群聊协商消息:', msg);
				return;
			}
			const fromUserId = msg.sendId;
			const content = this.parseRtcMessageContent(msg);
			if (msg.type === this.$enums.MESSAGE_TYPE.RTC_GROUP_OFFER) {
				this.$set(this.receivedOfferUserIds, String(fromUserId), true);
				await this.handleOffer(fromUserId, content);
			} else if (msg.type === this.$enums.MESSAGE_TYPE.RTC_GROUP_ANSWER) {
				await this.handleAnswer(fromUserId, content);
			} else if (msg.type === this.$enums.MESSAGE_TYPE.RTC_GROUP_CANDIDATE) {
				await this.handleCandidate(fromUserId, content);
			}
		},
		async flushPendingRtcMessages() {
			if (!this.signalingReady || !this.pendingRtcMessages.length) {
				return;
			}
			const messages = this.pendingRtcMessages.splice(0);
			for (const msg of messages) {
				if (!msg || String(msg.groupId) !== String(this.rtcInfo.groupId)) {
					continue;
				}
				try {
					await this.processNegotiationMessage(msg);
				} catch (error) {
					console.error('处理暂存的群聊协商消息失败:', msg, error);
				}
			}
		},
		shouldCreateOfferForPeer(peer) {
			if (!peer) {
				return false;
			}
			const peerId = String(peer.id);
			if (this.receivedOfferUserIds[peerId]) {
				return false;
			}
			const webrtc = this.webrtcInstances[peerId];
			const peerConnection = webrtc && webrtc.peerConnection;
			return !(peerConnection && peerConnection.remoteDescription && peerConnection.remoteDescription.type);
		},
		open(rtcInfo) {
			console.log('RtcGroupVideo.open called with:', rtcInfo);
			console.log('当前用户ID:', this.userStore.userInfo.id);
			console.log('所有用户信息:', rtcInfo.userInfos);
			console.log('群聊ID:', rtcInfo.groupId);
			console.log('是否为主持人:', rtcInfo.isHost);
			
			this.stopCallTimer();
			this.callDuration = 0;
			this.rtcInfo = rtcInfo;
			this.peers = [];
			this.pendingRtcMessages = Array.isArray(rtcInfo.pendingMessages) ? [...rtcInfo.pendingMessages] : [];
			this.receivedOfferUserIds = {};
			this.signalingReady = false;
			this.mergeActivePeers(Array.isArray(rtcInfo.userInfos) ? rtcInfo.userInfos : []);
			this.leftUsers = []; // 初始化离开用户列表
			this.rejectedUsers = []; // 初始化拒绝用户列表
			this.timeoutUsers = []; // 初始化超时用户列表
			console.log('过滤后的peers:', this.peers);
			console.log('peers长度:', this.peers.length);
			
			this.isShow = true;
			this.connectionStatus = '正在建立连接...';
			
			// 如果是发起者，开始呼叫
			if (this.rtcInfo.isHost) {
				console.log('是发起者，开始呼叫');
				this.onCall();
			} else {
				// 如果是参与者，等待加入
				console.log('是参与者，开始加入');
				this.state = "WAITING";
				this.onJoin(); // 添加这行，调用onJoin方法
			}
		},
		
		// 发起者开始呼叫
		async onCall() {
			//解决 分享屏幕后摄像头麦克风没有的问题
			this.isCleaning = false;

			console.log('=== 发起者开始呼叫 ===');
			console.log('发起者用户ID:', this.userStore.userInfo.id);
			console.log('群聊ID:', this.rtcInfo.groupId);
			console.log('参与者数量:', this.peers.length);
			console.log('参与者列表:', this.peers);
			
			try {
				// 获取本地媒体流
				this.localStream = await navigator.mediaDevices.getUserMedia({
					audio: true,
					video: true
				});
				
				console.log('获取到本地媒体流:', this.localStream.getTracks().map(t => t.kind));
				
				// 显示本地视频
					this.$nextTick(() => {
						const localVideo = this.$refs.localVideo;
						if (localVideo) {
							localVideo.srcObject = this.localStream;
							console.log('本地视频流已设置');
						}
					});
					
					// 调用后端API加入群聊
					this.connectionStatus = '正在加入群聊...';
					console.log('调用后端API加入群聊');
					await this.API.join(this.rtcInfo.groupId, this.userStore.userInfo.id, true);
					console.log('已成功加入群聊');
					this.state = "CHATING";
					this.connectionStatus = '已连接';
					this.startCallTimer();
					this.startHeartbeat();
					
					// 为每个参与者创建连接
					console.log('开始为参与者创建WebRTC连接...');
				this.peers.forEach(peer => {
					console.log(`为参与者 ${peer.nickName} (ID: ${peer.id}) 创建连接`);
					this.initPeerConnection(peer);
				});
				this.signalingReady = true;
				await this.flushPendingRtcMessages();
				
				// 向每个参与者发送Offer
				console.log('等待1秒后开始发送Offer...');
				setTimeout(() => {
					console.log('开始发送Offer消息...');
					this.peers.forEach(peer => {
						if (!this.shouldCreateOfferForPeer(peer)) {
							return;
						}
						console.log(`向参与者 ${peer.nickName} (ID: ${peer.id}) 发送Offer`);
						this.createOffer(peer);
					});
					}, 1000);
					
				} catch (error) {
					console.error('群聊发起失败:', error);
					const errorMessage = this.extractErrorMessage(error, '群聊通话启动失败');
					this.connectionStatus = '连接失败';
					this.stopCallTimer();
					this.cleanup();
					this.isShow = false;
					this.$message.error(errorMessage);
				}
			},
		
		// 参与者加入通话
		async onJoin() {
			//解决 分享屏幕后摄像头麦克风没有的问题
			this.isCleaning = false;

			console.log('参与者加入通话开始');
			console.log('当前peers:', this.peers);
			console.log('当前peers长度:', this.peers.length);
			console.log('当前用户ID:', this.userStore.userInfo.id);
			console.log('群聊ID:', this.rtcInfo.groupId);
			
			try {
				// 获取本地媒体流
				this.localStream = await navigator.mediaDevices.getUserMedia({
					audio: true,
					video: true
				});
				
				console.log('获取到本地媒体流:', this.localStream.getTracks().map(t => t.kind));
				
				// 显示本地视频
					this.$nextTick(() => {
						const localVideo = this.$refs.localVideo;
						if (localVideo) {
							localVideo.srcObject = this.localStream;
							console.log('本地视频流已设置');
						}
					});
					
					// 调用后端API加入群聊
					this.connectionStatus = '正在加入群聊...';
					console.log('调用后端API加入群聊');
					await this.API.join(this.rtcInfo.groupId, this.userStore.userInfo.id, false);
					console.log('已成功加入群聊');
					this.state = "CHATING";
					this.connectionStatus = '已连接';
					this.startCallTimer();
					this.startHeartbeat();
					
					// 获取当前群聊中的用户列表
					console.log('获取群聊用户列表');
				const response = await this.API.getGroupUsers(this.rtcInfo.groupId);
				const groupUsers = Array.isArray(response) ? response : [];
				console.log('群聊用户列表:', groupUsers);
				// 二次邀请消息只包含本次被邀请的人，必须以服务端实时在会列表补齐两个 App 用户
				this.mergeActivePeers(groupUsers);
				await this.$nextTick();
				
				// 为每个已在群聊中的用户创建连接（排除自己）
				this.peers.forEach(user => {
					console.log('处理群聊用户:', user);
					console.log('为群聊用户创建连接:', user);
					this.initPeerConnection(user);
				});
				this.signalingReady = true;
				await this.flushPendingRtcMessages();
				
				// 向每个已在群聊中的用户发送Offer（排除自己）
				setTimeout(() => {
					this.peers.forEach(user => {
						if (!this.shouldCreateOfferForPeer(user)) {
							console.log('对方已先发起协商，跳过重复Offer:', user.id);
							return;
						}
						console.log('向群聊用户发送Offer:', user);
						this.createOffer(user);
					});
				}, 1000);
				
					console.log('参与者加入通话完成');
					
				} catch (error) {
					console.error('参与者加入群聊失败:', error);
					const errorMessage = this.extractErrorMessage(error, '群聊通话已结束');
					this.connectionStatus = '连接失败';
					this.stopCallTimer();
					this.cleanup();
					this.isShow = false;
					this.$message.error(errorMessage);
				}
			},
		
		// 为单个参与者初始化WebRTC连接
		initPeerConnection(peer) {
			// 检查peer.id是否有效
			if (!peer || peer.id === undefined || peer.id === null) {
				console.error(`无效的peer对象或peer.id:`, peer);
				return null;
			}
			const peerId = String(peer.id);
			if (this.webrtcInstances[peerId]) {
				return this.webrtcInstances[peerId];
			}
			console.log(`为 ${peer.nickName} 初始化WebRTC连接`);
			console.log(`peer对象:`, peer);
			console.log(`peer.id:`, peer.id);
			
			// 创建WebRTC实例
			const webrtc = new ImWebRtc();
			webrtc.init(this.configuration);
			
			// 设置PeerConnection
			webrtc.setupPeerConnection((stream) => {
				console.log(`收到来自 ${peer.nickName} 的远程流`);
				console.log(`远程流轨道:`, stream.getTracks().map(t => t.kind));
				
				this.$nextTick(() => {
					const videoRefs = this.$refs[`remoteVideo-${peer.id}`];
					console.log(`查找视频元素: remoteVideo-${peer.id}`);
					console.log(`视频元素引用:`, videoRefs);
					
					// 处理数组情况
					const video = Array.isArray(videoRefs) ? videoRefs[0] : videoRefs;
					console.log(`最终视频元素:`, video);
					
					if (video) {
						// 移除muted属性，允许播放音频
						video.muted = false;
						video.srcObject = stream;
						console.log(`已设置 ${peer.nickName} 的视频流`);
						
						// 强制播放
						video.play().then(() => {
							console.log(`${peer.nickName} 视频播放成功`);
						}).catch(error => {
							console.error(`${peer.nickName} 视频播放失败:`, error);
						});
						
						// 添加更多调试信息
						video.onloadedmetadata = () => {
							console.log(`${peer.nickName} 视频元数据已加载`);
							console.log(`视频尺寸:`, video.videoWidth, 'x', video.videoHeight);
						};
						video.oncanplay = () => {
							console.log(`${peer.nickName} 视频可以播放`);
						};
						video.onplay = () => {
							console.log(`${peer.nickName} 视频开始播放`);
						};
						video.onerror = (error) => {
							console.error(`${peer.nickName} 视频错误:`, error);
						};
					} else {
						console.error(`未找到 ${peer.nickName} 的视频元素`);
						console.log(`所有refs:`, this.$refs);
					}
				});
			});
			
			// 监听候选信息
			webrtc.onIcecandidate((candidate) => {
				// 再次检查peer.id是否有效
				if (!peer || !peer.id) {
					console.error(`发送Candidate时peer.id无效:`, peer);
					return;
				}
				
				if (this.state == "CHATING") {
					// 连接已就绪,直接发送
					console.log(`发送Candidate到用户 ${peer.id} (${peer.nickName})`);
					this.sendCandidate(peer.id, candidate);
				} else {
					// 连接未就绪,缓存起来，连接后再发送
					if (!this.candidates[peer.id]) {
						this.candidates[peer.id] = [];
					}
					this.candidates[peer.id].push(candidate);
					console.log(`缓存Candidate到用户 ${peer.id} (${peer.nickName})`);
				}
			});
			
			// 监听连接状态
			webrtc.onStateChange((state) => {
				console.log(`与 ${peer.nickName} 的连接状态:`, state);
				if (state == "connected") {
					console.log(`与 ${peer.nickName} 的连接已建立`);
					// 发送缓存的候选信息
					if (this.candidates[peer.id]) {
						console.log(`发送缓存的 ${this.candidates[peer.id].length} 个Candidate到用户 ${peer.id}`);
						this.candidates[peer.id].forEach(candidate => {
							this.sendCandidate(peer.id, candidate);
						});
						this.candidates[peer.id] = [];
					}
				}
			});
			
			// 存储WebRTC实例
			this.$set(this.webrtcInstances, peerId, webrtc);
			console.log(`已存储WebRTC实例到 webrtcInstances[${peerId}]`);
			return webrtc;
		},
		



		
		// 创建Offer
		async createOffer(peer) {
			console.log(`=== 向 ${peer.nickName} (ID: ${peer.id}) 创建Offer ===`);
			
			const webrtc = this.webrtcInstances[peer.id];
			if (!webrtc) {
				console.error(`未找到 ${peer.nickName} 的WebRTC实例`);
				return;
			}
			
			try {
				// 设置本地流
				console.log(`设置本地流到WebRTC实例`);
				webrtc.setStream(this.localStream);
				
				// 创建Offer
				console.log(`开始创建Offer...`);
				const offer = await webrtc.createOffer();
				console.log(`Offer创建成功:`, offer);
				
				// 发送Offer
				console.log(`发送Offer到用户 ${peer.id}`);
				this.sendOffer(peer.id, offer);
				
			} catch (error) {
				console.error(`创建Offer失败:`, error);
			}
		},
		
		// 处理Offer
		async handleOffer(fromUserId, offer) {
			console.log(`处理来自 ${fromUserId} 的Offer`);
			
			let webrtc = this.webrtcInstances[fromUserId];
			if (!webrtc) {
				// 如果还没有WebRTC实例，创建一个
				const peer = this.peers.find(p => String(p.id) === String(fromUserId));
				if (peer) {
					this.initPeerConnection(peer);
					webrtc = this.webrtcInstances[fromUserId];
				} else {
					console.error(`未找到用户 ${fromUserId} 的peer信息`);
					return;
				}
			}
			
			try {
				const peerConnection = webrtc.peerConnection;
				if (peerConnection && peerConnection.signalingState === 'have-local-offer') {
					if (!this.shouldRollbackLocalOffer(fromUserId)) {
						console.warn(`收到 ${fromUserId} 的Offer时本地已有Offer，本端优先，忽略远端Offer`);
						return;
					}
					console.warn(`收到 ${fromUserId} 的Offer时本地已有Offer，回滚本地描述`);
					try {
						await peerConnection.setLocalDescription({ type: 'rollback' });
						console.log(`本地Offer回滚成功: ${fromUserId}`);
					} catch (rollbackError) {
						console.warn(`本地Offer回滚失败，继续处理远端Offer: ${fromUserId}`, rollbackError);
					}
				}

				// 设置本地流
				webrtc.setStream(this.localStream);
				
				// 设置远程描述
				await webrtc.setRemoteDescription(offer);
				await this.flushPendingRemoteCandidates(fromUserId);
				
				// 创建Answer
				const answer = await webrtc.createAnswer();
				
				// 发送Answer
				this.sendAnswer(fromUserId, answer);
				
			} catch (error) {
				console.error('处理Offer失败:', error);
			}
		},
		
		// 处理Answer
		async handleAnswer(fromUserId, answer) {
			console.log(`处理来自 ${fromUserId} 的Answer`);
			
			const webrtc = this.webrtcInstances[fromUserId];
			if (webrtc) {
				try {
					const peerConnection = webrtc.peerConnection;
					if (peerConnection && peerConnection.signalingState !== 'have-local-offer') {
						console.warn(`当前信令状态为 ${peerConnection.signalingState}，忽略用户 ${fromUserId} 的过期Answer`);
						return;
					}
					await webrtc.setRemoteDescription(answer);
					await this.flushPendingRemoteCandidates(fromUserId);
					console.log(`成功设置远程描述 (Answer)`);
				} catch (error) {
					console.error('处理Answer失败:', error);
				}
			} else {
				console.error(`未找到用户 ${fromUserId} 的WebRTC实例 (Answer)`);
			}
		},
		
		// 处理Candidate
		async handleCandidate(fromUserId, candidate) {
			console.log(`处理来自 ${fromUserId} 的Candidate`);
			
			let webrtc = this.webrtcInstances[fromUserId];
			if (!webrtc) {
				// 如果还没有WebRTC实例，创建一个
				const peer = this.peers.find(p => String(p.id) === String(fromUserId));
				if (peer) {
					this.initPeerConnection(peer);
					webrtc = this.webrtcInstances[fromUserId];
				} else {
					console.error(`未找到用户 ${fromUserId} 的peer信息 (Candidate)`);
					return;
				}
			}
			
			if (webrtc) {
				try {
					const peerConnection = webrtc.peerConnection;
					if (!peerConnection || !peerConnection.remoteDescription || !peerConnection.remoteDescription.type) {
						this.cachePendingRemoteCandidate(fromUserId, candidate);
						return;
					}
					await webrtc.addIceCandidate(candidate);
					console.log(`成功添加ICE候选`);
				} catch (error) {
					console.error('处理Candidate失败:', error);
				}
			}
		},

		shouldRollbackLocalOffer(fromUserId) {
			const localId = Number(this.userStore.userInfo.id);
			const remoteId = Number(fromUserId);
			if (Number.isFinite(localId) && Number.isFinite(remoteId) && localId !== remoteId) {
				return localId > remoteId;
			}
			return String(this.userStore.userInfo.id) > String(fromUserId);
		},

		cachePendingRemoteCandidate(fromUserId, candidate) {
			if (!candidate) {
				return;
			}
			if (!this.pendingRemoteCandidates[fromUserId]) {
				this.$set(this.pendingRemoteCandidates, fromUserId, []);
			}
			this.pendingRemoteCandidates[fromUserId].push(candidate);
			console.log(`远端描述未就绪，缓存用户 ${fromUserId} 的Candidate，当前数量: ${this.pendingRemoteCandidates[fromUserId].length}`);
		},

		async flushPendingRemoteCandidates(fromUserId) {
			const candidates = this.pendingRemoteCandidates[fromUserId];
			if (!candidates || !candidates.length) {
				return;
			}
			const webrtc = this.webrtcInstances[fromUserId];
			const peerConnection = webrtc && webrtc.peerConnection;
			if (!peerConnection || !peerConnection.remoteDescription || !peerConnection.remoteDescription.type) {
				return;
			}
			console.log(`开始补加用户 ${fromUserId} 缓存的Candidate，数量: ${candidates.length}`);
			while (candidates.length) {
				const candidate = candidates.shift();
				try {
					await webrtc.addIceCandidate(candidate);
					console.log(`补加用户 ${fromUserId} 的Candidate成功`);
				} catch (error) {
					console.error(`补加用户 ${fromUserId} 的Candidate失败:`, error);
				}
			}
			this.$delete(this.pendingRemoteCandidates, fromUserId);
		},
		
		// 发送Offer
		sendOffer(targetUserId, offer) {
			console.log(`=== 发送Offer ===`);
			console.log(`群聊ID:`, this.rtcInfo.groupId);
			console.log(`目标用户ID:`, targetUserId);
			console.log(`Offer内容:`, offer);
			
			this.API.offer(this.rtcInfo.groupId, targetUserId, offer).then(response => {
				console.log(`Offer发送成功:`, response);
			}).catch(error => {
				console.error('发送Offer失败:', error);
			});
		},
		
		// 发送Answer
		sendAnswer(targetUserId, answer) {
			this.API.answer(this.rtcInfo.groupId, targetUserId, answer).catch(error => {
				console.error('发送Answer失败:', error);
			});
		},
		
		// 发送Candidate
		sendCandidate(targetUserId, candidate) {
			this.API.candidate(this.rtcInfo.groupId, targetUserId, candidate).catch(error => {
				console.error('发送Candidate失败:', error);
			});
		},
		
		// 处理来自后端的WebRTC消息
		onRTCMessage(msg) {
			if (!msg) {
				return;
			}
			console.log('=== RtcGroupVideo收到WebRTC消息 ===');
			console.log('消息类型:', msg.type);
			console.log('发送者ID:', msg.sendId);
			console.log('群聊ID:', msg.groupId);
			console.log('消息内容:', msg.content);
			
			try {
				if (!this.rtcInfo || String(msg.groupId) !== String(this.rtcInfo.groupId)) {
					console.log('忽略其他群聊或无效的WebRTC消息:', msg);
					return;
				}
				const fromUserId = msg.sendId;
				
				// 排除自己发送的消息
				if (String(fromUserId) === String(this.userStore.userInfo.id)) {
					console.log('忽略自己发送的消息:', fromUserId);
					return;
				}
				
				if (this.isNegotiationMessage(msg)) {
					if (!this.signalingReady || !this.localStream || this.state !== 'CHATING') {
						this.queueRtcMessage(msg);
						return;
					}
					this.processNegotiationMessage(msg).catch(error => {
						console.error('处理群聊协商消息失败:', msg, error);
					});
				} else if (msg.type === this.$enums.MESSAGE_TYPE.RTC_GROUP_QUIT) {
					console.log('处理用户离开消息');
					// RTC_GROUP_QUIT消息不需要解析JSON内容
					this.handleUserQuit(fromUserId);
				} else if (msg.type === this.$enums.MESSAGE_TYPE.RTC_GROUP_NETWORK_QUIT) {
					console.log('处理用户网络异常退出消息');
					this.handleUserQuit(fromUserId, true, msg.sendNickName);
				} else if (msg.type === this.$enums.MESSAGE_TYPE.RTC_GROUP_REJECT) {
					console.log('处理用户拒绝消息');
					this.handleUserReject(fromUserId);
					} else if (msg.type === this.$enums.MESSAGE_TYPE.RTC_GROUP_TIMEOUT) {
						console.log('处理用户超时消息');
						this.handleUserTimeout(fromUserId);
					} else if (msg.type === this.$enums.MESSAGE_TYPE.RTC_GROUP_CANCEL) {
						console.log('处理群聊取消消息');
						this.handleGroupCancel(msg);
					} else {
						console.log('未知的消息类型:', msg.type);
					}
				} catch (error) {
					console.error('处理WebRTC消息失败:', error);
				}
			},
			handleGroupCancel(msg) {
				let message = '群聊通话已取消';
				try {
					const content = msg && msg.content ? JSON.parse(msg.content) : null;
					if (content && content.action === 'auto_end') {
						message = '群聊通话已自动结束';
					}
				} catch (error) {
					console.warn('解析群聊结束原因失败:', error);
				}
				this.closeByRemoteCancel(message);
			},
			
			// 处理用户离开
			handleUserQuit(userId, networkTimeout = false, fallbackNickName = '') {
			console.log(`用户 ${userId} 离开了群聊`);
			
			// 找到离开的用户信息
			const leftUser = this.peers.find(peer => String(peer.id) === String(userId));
			if (leftUser) {
				console.log(`用户 ${leftUser.nickName} 离开了群聊`);
				
				// 关闭该用户的WebRTC连接
				const peerKey = Object.keys(this.webrtcInstances)
					.find(key => String(key) === String(userId)) || userId;
				const webrtc = this.webrtcInstances[peerKey];
				if (webrtc) {
					webrtc.close();
					delete this.webrtcInstances[peerKey];
					console.log(`已关闭与 ${leftUser.nickName} 的WebRTC连接`);
				}
				
				// 清空该用户的视频流
				this.$nextTick(() => {
					const videoRefs = this.$refs[`remoteVideo-${userId}`];
					const video = Array.isArray(videoRefs) ? videoRefs[0] : videoRefs;
					if (video) {
						video.srcObject = null;
						console.log(`已清空 ${leftUser.nickName} 的视频流`);
					}
				});
				
				// 从peers列表中移除该用户，并添加到leftUsers列表
				this.peers = this.peers.filter(peer => String(peer.id) !== String(userId));
				this.leftUsers.push(leftUser);
				
				// 显示用户离开的提示
				this.$message[networkTimeout ? 'warning' : 'info'](
					networkTimeout ? `${leftUser.nickName}因网络异常已退出` : `${leftUser.nickName} 已离开群聊`
				);
				
				// 更新连接状态
				this.connectionStatus = `已连接 (${this.peers.length + 1}人)`;
			} else if (networkTimeout) {
				this.$message.warning(`${fallbackNickName || '对方'}因网络异常已退出`);
			}
		},

		// 处理用户拒绝群聊邀请
		handleUserReject(userId) {
			console.log(`用户 ${userId} 拒绝了群聊邀请`);
			
			// 找到拒绝的用户信息
			const rejectUser = this.peers.find(peer => peer.id === userId);
			if (rejectUser) {
				console.log(`用户 ${rejectUser.nickName} 拒绝了群聊邀请`);
				
				// 关闭该用户的WebRTC连接（如果存在）
				const webrtc = this.webrtcInstances[userId];
				if (webrtc) {
					webrtc.close();
					delete this.webrtcInstances[userId];
					console.log(`已关闭与 ${rejectUser.nickName} 的WebRTC连接`);
				}
				
				// 清空该用户的视频流
				this.$nextTick(() => {
					const videoRefs = this.$refs[`remoteVideo-${userId}`];
					const video = Array.isArray(videoRefs) ? videoRefs[0] : videoRefs;
					if (video) {
						video.srcObject = null;
						console.log(`已清空 ${rejectUser.nickName} 的视频流`);
					}
				});
				
				// 从peers列表中移除该用户，并添加到rejectedUsers列表
				this.peers = this.peers.filter(peer => peer.id !== userId);
				this.rejectedUsers.push(rejectUser);
				
				// 显示用户拒绝的提示
				this.$message.warning(`${rejectUser.nickName} 已拒绝群聊邀请`);
				
				// 更新连接状态
				this.connectionStatus = `已连接 (${this.peers.length + 1}人)`;
			}
		},

		// 处理用户超时未接听
		handleUserTimeout(userId) {
			console.log(`用户 ${userId} 超时未接听群聊邀请`);
			
			// 找到超时的用户信息
			const timeoutUser = this.peers.find(peer => peer.id === userId);
			if (timeoutUser) {
				console.log(`用户 ${timeoutUser.nickName} 超时未接听群聊邀请`);
				
				// 关闭该用户的WebRTC连接（如果存在）
				const webrtc = this.webrtcInstances[userId];
				if (webrtc) {
					webrtc.close();
					delete this.webrtcInstances[userId];
					console.log(`已关闭与 ${timeoutUser.nickName} 的WebRTC连接`);
				}
				
				// 清空该用户的视频流
				this.$nextTick(() => {
					const videoRefs = this.$refs[`remoteVideo-${userId}`];
					const video = Array.isArray(videoRefs) ? videoRefs[0] : videoRefs;
					if (video) {
						video.srcObject = null;
						console.log(`已清空 ${timeoutUser.nickName} 的视频流`);
					}
				});
				
				// 从peers列表中移除该用户，并添加到timeoutUsers列表
				this.peers = this.peers.filter(peer => peer.id !== userId);
				this.timeoutUsers.push(timeoutUser);
				
				// 显示用户超时的提示
				this.$message.warning(`${timeoutUser.nickName} 超时未接听群聊邀请`);
				
				// 更新连接状态
				this.connectionStatus = `已连接 (${this.peers.length + 1}人)`;
			}
		},
		
		// 控制功能
		toggleCamera() {
			if (this.localStream) {
				const videoTrack = this.localStream.getVideoTracks()[0];
				if (videoTrack) {
					videoTrack.enabled = !videoTrack.enabled;
					this.isCamera = videoTrack.enabled;
				}
			}
		},
		
		toggleMicrophone() {
			if (this.localStream) {
				const audioTrack = this.localStream.getAudioTracks()[0];
				if (audioTrack) {
					audioTrack.enabled = !audioTrack.enabled;
					this.isMicroPhone = audioTrack.enabled;
				}
			}
		},
		
		// 切换屏幕分享
		async toggleScreenShare() {
			console.log('=== 开始切换屏幕分享 ===');
			console.log('当前屏幕分享状态:', this.isScreenSharing);
			
			try {
				if (!this.isScreenSharing) {
					// 开始屏幕分享
					console.log('准备开始屏幕分享...');
					
					// 显示加载状态
					this.$message.info('正在启动屏幕分享...');
					
					// 请求屏幕分享权限
					const stream = await navigator.mediaDevices.getDisplayMedia({
						video: {
							cursor: "always",
							displaySurface: "monitor",
							// 新增：限制分辨率和帧率，提高兼容性
							width: { ideal: 1920, max: 1920 },
							height: { ideal: 1080, max: 1080 },
							frameRate: { ideal: 30, max: 30 },
							// 新增：指定内容类型
							contentType: "screen"
						},
						audio: false // 屏幕分享通常不需要音频
					});
					
					console.log('屏幕分享流获取成功:', stream);
					console.log('流轨道信息:', stream.getTracks().map(t => ({
						kind: t.kind,
						enabled: t.enabled,
						readyState: t.readyState,
						label: t.label
					})));
					
					// 保存屏幕分享流
					this.screenStream = stream;
					this.isScreenSharing = true;
					
					// 检测屏幕分享类型
					const shareType = this.detectScreenShareType(stream);
					console.log('检测到屏幕分享类型:', shareType);
					
					// 将屏幕分享流设置为本地流
					console.log('开始设置屏幕分享流...');
					await this.setLocalStream(stream);
					
					// 监听屏幕分享停止事件
					stream.getVideoTracks()[0].addEventListener('ended', () => {
						console.log('屏幕分享被用户手动停止');
						this.stopScreenShare();
					});
					
					// 新增：通知uniapp端屏幕分享开始，包含类型信息
					this.notifyScreenShareStatus(true, shareType);
					
					console.log('✅ 屏幕分享开始成功');
					this.$message.success('屏幕分享已开始，其他用户将看到您的屏幕');
					
				} else {
					// 停止屏幕分享
					console.log('准备停止屏幕分享...');
					await this.stopScreenShare();
				}
				
			} catch (error) {
				console.error('❌ 屏幕分享操作失败:', error);
				console.error('错误详情:', {
					name: error.name,
					message: error.message,
					stack: error.stack
				});
				
				// 重置状态
				this.isScreenSharing = false;
				this.screenStream = null;
				
				// 显示错误提示
				this.$message.error('屏幕分享失败: ' + error.message);
			}
		},
		
		// 停止屏幕分享
		async stopScreenShare() {
			console.log('=== 开始停止屏幕分享 ===');
			
			// 检查组件状态
			if (!this.isShow || this.isCleaning) {
				console.log('组件已关闭或正在清理，仅清理屏幕分享资源');
				
				if (this.screenStream) {
					this.screenStream.getTracks().forEach(track => {
						track.stop();
					});
					this.screenStream = null;
				}
				this.isScreenSharing = false;
				return;
			}
			
			try {
				// 显示加载状态
				this.$message.info('正在停止屏幕分享...');
				
				if (this.screenStream) {
					// 停止所有轨道
					this.screenStream.getTracks().forEach(track => {
						track.stop();
						console.log('屏幕分享轨道已停止:', track.kind);
					});
					
					this.screenStream = null;
					console.log('屏幕分享流已清理');
				}
				
				// 重置状态
				this.isScreenSharing = false;
				
				// 新增：检查是否正在清理或已关闭
				if (this.isCleaning || !this.isShow) {
					console.log('组件正在清理或已关闭，跳过恢复摄像头流');
					return;
				}
				
				// 只有在正常状态下才恢复摄像头流
				console.log('重新获取摄像头流...');
				const stream = await navigator.mediaDevices.getUserMedia({
					audio: true,
					video: true
				});
				
				// 设置新的摄像头流
				console.log('设置新的摄像头流...');
				await this.setLocalStream(stream);
				
				// 新增：通知uniapp端屏幕分享停止
				this.notifyScreenShareStatus(false);
				
				console.log('✅ 摄像头流已恢复');
				this.$message.success('已恢复摄像头，其他用户将看到您的摄像头画面');
				
				console.log('✅ 屏幕分享已停止');
				
			} catch (error) {
				console.error('❌ 停止屏幕分享失败:', error);
				this.$message.error('停止屏幕分享失败: ' + error.message);
			}
		},
		
		// 检测屏幕分享类型
		detectScreenShareType(stream) {
			console.log('=== 检测屏幕分享类型 ===');
			
			try {
				const videoTrack = stream.getVideoTracks()[0];
				if (videoTrack) {
					const settings = videoTrack.getSettings();
					const label = videoTrack.label.toLowerCase();
					
					console.log('视频轨道设置:', settings);
					console.log('视频轨道标签:', label);
					
					// 根据标签判断分享类型
					if (label.includes('entire screen') || label.includes('full screen') || label.includes('monitor')) {
						return 'fullscreen';
					} else if (label.includes('window') || label.includes('application') || label.includes('chrome')) {
						return 'window';
					} else if (label.includes('tab') || label.includes('browser')) {
						return 'browser';
					} else {
						return 'unknown';
					}
				}
			} catch (error) {
				console.error('检测屏幕分享类型失败:', error);
			}
			
			return 'unknown';
		},
		
		// 通知uniapp端屏幕分享状态变化
		notifyScreenShareStatus(isScreenSharing, shareType = 'unknown') {
			console.log('=== 通知uniapp端屏幕分享状态变化 ===');
			console.log('屏幕分享状态:', isScreenSharing);
			
			// 通过父组件向uniapp发送消息
			if (this.$parent && this.$parent.sendMessageToUniApp) {
				this.$parent.sendMessageToUniApp('SCREEN_SHARE_STATUS', {
					isScreenSharing: isScreenSharing,
					streamType: isScreenSharing ? 'screen' : 'camera',
					shareType: shareType, // 新增：屏幕分享类型
					userId: this.userStore.userInfo.id,
					groupId: this.rtcInfo.groupId
				});
			} else {
				console.log('父组件未提供sendMessageToUniApp方法，尝试通过事件总线发送');
				// 尝试通过事件总线发送
				if (this.$eventBus) {
					this.$eventBus.$emit('SCREEN_SHARE_STATUS', {
						isScreenSharing: isScreenSharing,
						streamType: isScreenSharing ? 'screen' : 'camera',
						shareType: shareType, // 新增：屏幕分享类型
						userId: this.userStore.userInfo.id,
						groupId: this.rtcInfo.groupId
					});
				}
			}
		},
		
		// 设置本地流
		async setLocalStream(stream) {
			console.log('设置本地流:', stream);
			
			if (!stream) {
				console.error('流为空，无法设置');
				return;
			}
			
			// 停止旧的本地流
			if (this.localStream) {
				this.localStream.getTracks().forEach(track => {
					track.stop();
				});
			}
			
			// 设置新的本地流
			this.localStream = stream;
			
			// 更新本地视频显示
			this.$nextTick(() => {
				const localVideo = this.$refs.localVideo;
				if (localVideo) {
					localVideo.srcObject = stream;
					console.log('本地视频流已更新');
				}
			});
			
			// 更新所有WebRTC连接的本地流并重新协商
			console.log('开始更新所有WebRTC连接的本地流...');
			const updatePromises = Object.entries(this.webrtcInstances).map(async ([peerId, webrtc]) => {
				if (webrtc && typeof webrtc.setLocalStream === 'function') {
					try {
						console.log(`更新与用户 ${peerId} 的本地流`);
						webrtc.setLocalStream(stream);
						
						// 如果连接已建立，需要重新协商
						if (webrtc.getConnectionState && webrtc.getConnectionState() === 'connected') {
							console.log(`与用户 ${peerId} 的连接已建立，开始重新协商...`);
							
							// 创建新的Offer
							const offer = await webrtc.createOffer();
							if (offer) {
								console.log(`向用户 ${peerId} 发送新的Offer`);
								await this.sendOffer(peerId, offer);
							}
						}
					} catch (error) {
						console.error(`更新用户 ${peerId} 的本地流失败:`, error);
					}
				}
			});
			
			await Promise.all(updatePromises);
			console.log('✅ 本地流设置完成，所有连接已更新');
		},
		
		handleClose() {
			console.log('=== 开始挂断操作 ===');
			
			// 自动关闭设置对话框（让用户看不见）
			this.autoCloseSettingDialog();
			
			// 1. 先清理所有资源
			this.cleanup();
			
			// 2. 调用后端API离开群聊
			if (this.rtcInfo && this.rtcInfo.groupId) {
				console.log('调用后端API离开群聊');
				this.API.leaveGroup(this.rtcInfo.groupId, this.userStore.userInfo.id).then(() => {
					console.log('已成功离开群聊');
				}).catch(error => {
					console.error('离开群聊失败:', error);
				});
			}
			
			// 3. 延迟关闭界面，确保资源完全清理
			setTimeout(() => {
				this.isShow = false;
				console.log('✅ 界面已关闭');
			}, 100);
		},
		
		cleanup() {
			console.log('=== 开始彻底清理WebRTC资源 ===');
			
			// 设置清理状态标志
			this.isCleaning = true;
			this.stopHeartbeat();
			
			// 1. 清理屏幕分享资源
			if (this.isScreenSharing) {
				console.log('清理屏幕分享资源...');
				this.stopScreenShare(); // 现在会检查isCleaning标志
			}
			
			// 2. 彻底关闭所有WebRTC实例
			Object.entries(this.webrtcInstances).forEach(([peerId, webrtc]) => {
				if (webrtc) {
					console.log(`关闭与用户 ${peerId} 的WebRTC连接`);
					try {
						// 关闭所有数据通道
						if (webrtc.dataChannel) {
							webrtc.dataChannel.close();
						}
						
						// 关闭RTCPeerConnection
						if (webrtc.pc) {
							webrtc.pc.close();
						}
						
						// 调用自定义的close方法
						if (typeof webrtc.close === 'function') {
							webrtc.close();
						}
						
						console.log(`✅ 用户 ${peerId} 的WebRTC连接已关闭`);
					} catch (error) {
						console.error(`❌ 关闭用户 ${peerId} 的WebRTC连接失败:`, error);
					}
				}
			});
			this.webrtcInstances = {};
			
			// 3. 清理候选信息缓存
			this.candidates = {};
			this.pendingRemoteCandidates = {};
			this.pendingRtcMessages = [];
			this.receivedOfferUserIds = {};
			this.signalingReady = false;
			
			// 4. 彻底停止本地媒体流
			if (this.localStream) {
				console.log('停止本地媒体流...');
				this.localStream.getTracks().forEach(track => {
					track.stop();
					console.log(`✅ 轨道 ${track.kind} 已停止`);
				});
				this.localStream = null;
			}
			
			// 5. 清理所有远程视频元素
			console.log('清理所有远程视频元素...');
			this.peers.forEach(peer => {
				const videoRefs = this.$refs[`remoteVideo-${peer.id}`];
				if (videoRefs) {
					const video = Array.isArray(videoRefs) ? videoRefs[0] : videoRefs;
					if (video) {
						// 暂停视频
						video.pause();
						// 清空srcObject
						video.srcObject = null;
						// 移除所有事件监听器
						video.onloadedmetadata = null;
						video.oncanplay = null;
						video.onplay = null;
						video.onerror = null;
						// 重置视频属性
						video.currentTime = 0;
						video.load();
						console.log(`✅ 用户 ${peer.id} 的远程视频已清理`);
					}
				}
			});
			
			// 6. 清理本地视频元素
			if (this.$refs.localVideo) {
				console.log('清理本地视频元素...');
				const localVideo = this.$refs.localVideo;
				localVideo.pause();
				localVideo.srcObject = null;
				localVideo.onloadedmetadata = null;
				localVideo.oncanplay = null;
				localVideo.onplay = null;
				localVideo.onerror = null;
				localVideo.currentTime = 0;
				localVideo.load();
				console.log('✅ 本地视频已清理');
			}
			

			
			// 7. 重置所有状态
			this.state = "CLOSED";
			this.connectionStatus = '已断开';
			this.stopCallTimer();
			this.callDuration = 0;
			this.leftUsers = [];
			this.rejectedUsers = [];
			this.timeoutUsers = [];
			this.isScreenSharing = false;
			this.screenStream = null;
			
			// 8. 清理最大化状态
			this.isMaximized = false;
			this.maximizedVideo = null;
			this.maximizedVideoType = '';
			this.maximizedVideoId = '';
			
			// 9. 清理全屏视频（如果存在）
			this.closeFullscreenVideo();
			
			// 10. 清理peers数组
			this.peers = [];
			
			// 11. 强制垃圾回收（如果浏览器支持）
			if (window.gc) {
				console.log('强制垃圾回收...');
				window.gc();
			}
			
			console.log('✅ WebRTC资源彻底清理完成');
		},
		
		onVideoLoaded(videoId, type) {
			console.log(`${type} 视频已加载:`, videoId);
		},
		
		onVideoError(videoId, type) {
			console.error(`${type} 视频加载失败:`, videoId);
		},

		onVideoCanPlay(videoId, type) {
			console.log(`${type} 视频可以播放:`, videoId);
		},

		onVideoPlaying(videoId, type) {
			console.log(`${type} 视频开始播放:`, videoId);
		},
		
		// 处理键盘事件
		handleKeydown(event) {
			// 如果最大化对话框打开，按ESC键关闭
			if (this.isMaximized && event.key === 'Escape') {
				this.closeMaximizedVideo();
			}
			
			// 如果全屏视频打开，按ESC键关闭
			if (document.getElementById('fullscreen-video') && event.key === 'Escape') {
				this.closeFullscreenVideo();
			}
			
			// 防止意外的设置对话框触发
			// 检查是否在视频通话中，如果是则阻止设置相关的快捷键
			if (this.rtcInfo && this.rtcInfo.groupId) {
				// 阻止Ctrl+, 或 Cmd+, 等可能的设置快捷键
				if ((event.ctrlKey || event.metaKey) && event.key === ',') {
					event.preventDefault();
					event.stopPropagation();
					console.log('阻止了设置快捷键触发');
				}
				
				// 阻止可能的语音录制快捷键
				// 阻止Ctrl+R 或 Cmd+R 等可能的录音快捷键
				if ((event.ctrlKey || event.metaKey) && event.key === 'r') {
					event.preventDefault();
					event.stopPropagation();
					console.log('阻止了语音录制快捷键触发');
				}
				
				// 阻止空格键等可能的录音快捷键
				if (event.key === ' ' && event.target.tagName !== 'INPUT' && event.target.tagName !== 'TEXTAREA') {
					event.preventDefault();
					event.stopPropagation();
					console.log('阻止了空格键录音快捷键触发');
				}
				
				// 阻止更多可能的设置相关快捷键
				// 阻止Ctrl+S 或 Cmd+S 等可能的设置快捷键
				if ((event.ctrlKey || event.metaKey) && event.key === 's') {
					event.preventDefault();
					event.stopPropagation();
					console.log('阻止了设置保存快捷键触发');
				}
				
				// 阻止F1-F12等可能的帮助/设置快捷键
				if (event.key >= 'F1' && event.key <= 'F12') {
					event.preventDefault();
					event.stopPropagation();
					console.log('阻止了功能键快捷键触发');
				}
			}
		},
		
		// 最大化视频 - 实现真正的全屏
		maximizeVideo(videoId, type) {
			console.log('=== 开始最大化视频 ===');
			console.log(`视频ID: ${videoId}, 类型: ${type}`);
			
			// 获取视频元素
			let video;
			if (type === 'local') {
				video = this.$refs.localVideo;
			} else {
				const videoRefs = this.$refs[`remoteVideo-${videoId}`];
				video = Array.isArray(videoRefs) ? videoRefs[0] : videoRefs;
			}
			
			if (!video) {
				console.error('未找到视频元素');
				return;
			}
			
			// 获取视频流
			const videoStream = video.srcObject;
			if (!videoStream) {
				console.error('视频流为空');
				return;
			}
			
			// 设置最大化状态
			this.maximizedVideo = videoStream;
			this.maximizedVideoType = type;
			this.maximizedVideoId = videoId;
			this.isMaximized = true;
			
			// 创建全屏视频元素
			this.createFullscreenVideo(videoStream, type);
		},
		
		// 创建全屏视频
		createFullscreenVideo(videoStream, type) {
			// 移除已存在的全屏视频元素
			const existingFullscreen = document.getElementById('fullscreen-video');
			if (existingFullscreen) {
				existingFullscreen.remove();
			}
			
			// 创建全屏视频容器
			const fullscreenContainer = document.createElement('div');
			fullscreenContainer.id = 'fullscreen-video';
			fullscreenContainer.style.cssText = `
				position: fixed;
				top: 0;
				left: 0;
				width: 100vw;
				height: 100vh;
				background: #000;
				z-index: 999999;
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
			`;
			
			// 创建视频元素
			const fullscreenVideo = document.createElement('video');
			fullscreenVideo.autoplay = true;
			fullscreenVideo.muted = (type === 'local');
			fullscreenVideo.playsInline = true;
			fullscreenVideo.style.cssText = `
				width: 100%;
				height: 100%;
				object-fit: contain;
				background: transparent;
			`;
			
			// 创建控制按钮
			const controlBar = document.createElement('div');
			controlBar.style.cssText = `
				position: absolute;
				bottom: 20px;
				left: 50%;
				transform: translateX(-50%);
				display: flex;
				gap: 15px;
				z-index: 1000000;
			`;
			
			// 关闭全屏按钮
			const closeBtn = document.createElement('button');
			closeBtn.textContent = '关闭全屏';
			closeBtn.style.cssText = `
				padding: 12px 24px;
				background: #f56c6c;
				color: white;
				border: none;
				border-radius: 6px;
				font-size: 16px;
				cursor: pointer;
				transition: background 0.3s;
			`;
			closeBtn.onmouseover = () => closeBtn.style.background = '#f78989';
			closeBtn.onmouseout = () => closeBtn.style.background = '#f56c6c';
			closeBtn.onclick = () => this.closeFullscreenVideo();
			
			// 退出全屏按钮
			const exitFullscreenBtn = document.createElement('button');
			exitFullscreenBtn.textContent = '退出全屏';
			exitFullscreenBtn.style.cssText = `
				padding: 12px 24px;
				background: #409EFF;
				color: white;
				border: none;
				border-radius: 6px;
				font-size: 16px;
				cursor: pointer;
				transition: background 0.3s;
			`;
			exitFullscreenBtn.onmouseover = () => exitFullscreenBtn.style.background = '#66b1ff';
			exitFullscreenBtn.onmouseout = () => exitFullscreenBtn.style.background = '#409EFF';
			exitFullscreenBtn.onclick = () => this.exitFullscreen();
			
			// 组装控制栏
			controlBar.appendChild(closeBtn);
			controlBar.appendChild(exitFullscreenBtn);
			
			// 组装全屏容器
			fullscreenContainer.appendChild(fullscreenVideo);
			fullscreenContainer.appendChild(controlBar);
			
			// 添加到页面
			document.body.appendChild(fullscreenContainer);
				
				// 设置视频流
			fullscreenVideo.srcObject = videoStream;
				
			// 播放视频
			fullscreenVideo.play().then(() => {
				console.log('全屏视频开始播放');
				// 请求浏览器全屏
				this.requestFullscreen(fullscreenContainer);
				}).catch(error => {
				console.error('全屏视频播放失败:', error);
				// 即使播放失败也尝试请求全屏
				this.requestFullscreen(fullscreenContainer);
			});
			
			// 监听全屏状态变化
			document.addEventListener('fullscreenchange', this.onFullscreenChange);
			document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
			document.addEventListener('mozfullscreenchange', this.onFullscreenChange);
			document.addEventListener('MSFullscreenChange', this.onFullscreenChange);
		},
		
		// 关闭全屏视频
		closeFullscreenVideo() {
			const fullscreenContainer = document.getElementById('fullscreen-video');
			if (fullscreenContainer) {
				fullscreenContainer.remove();
			}
			
			// 重置状态
			this.isMaximized = false;
			this.maximizedVideo = null;
			this.maximizedVideoType = '';
			this.maximizedVideoId = '';
			
			// 移除事件监听器
			document.removeEventListener('fullscreenchange', this.onFullscreenChange);
			document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
			document.removeEventListener('mozfullscreenchange', this.onFullscreenChange);
			document.removeEventListener('MSFullscreenChange', this.onFullscreenChange);
		},
		
		// 退出全屏
		exitFullscreen() {
			if (document.fullscreenElement || 
				document.webkitFullscreenElement || 
				document.mozFullScreenElement || 
				document.msFullscreenElement) {
				
				if (document.exitFullscreen) {
					document.exitFullscreen();
				} else if (document.webkitExitFullscreen) {
					document.webkitExitFullscreen();
				} else if (document.mozCancelFullScreen) {
					document.mozCancelFullScreen();
				} else if (document.msExitFullscreen) {
					document.msExitFullscreen();
				}
			}
		},
		
		// 全屏状态变化处理
		onFullscreenChange() {
			if (!document.fullscreenElement && 
				!document.webkitFullscreenElement && 
				!document.mozFullScreenElement && 
				!document.msFullscreenElement) {
				// 退出全屏时，关闭全屏视频
				this.closeFullscreenVideo();
			}
		},
		
		// 请求浏览器全屏
		requestFullscreen(element) {
			if (element.requestFullscreen) {
				element.requestFullscreen().catch(err => {
					console.log('全屏请求失败:', err);
				});
			} else if (element.webkitRequestFullscreen) {
				element.webkitRequestFullscreen().catch(err => {
					console.log('webkit全屏请求失败:', err);
				});
			} else if (element.mozRequestFullScreen) {
				element.mozRequestFullScreen().catch(err => {
					console.log('moz全屏请求失败:', err);
				});
			} else if (element.msRequestFullscreen) {
				element.msRequestFullscreen().catch(err => {
					console.log('ms全屏请求失败:', err);
				});
			}
		},
		

		
		// 关闭最大化视频
		closeMaximizedVideo() {
			console.log('=== 开始关闭最大化视频 ===');
			
			try {
				// 关闭全屏视频（如果存在）
				this.closeFullscreenVideo();
				

				
				// 重置状态
				this.isMaximized = false;
				this.maximizedVideo = null;
				this.maximizedVideoType = '';
				this.maximizedVideoId = '';
				
				console.log('✅ 最大化视频已关闭');
				
			} catch (error) {
				console.error('❌ 关闭最大化视频时出错:', error);
				// 强制重置状态
				this.isMaximized = false;
				this.maximizedVideo = null;
				this.maximizedVideoType = '';
				this.maximizedVideoId = '';
			}
		},
		
		// 强制关闭最大化视频（备用方法）
		forceCloseMaximized() {
			console.log('=== 强制关闭最大化视频 ===');
			
			// 关闭全屏视频（如果存在）
			this.closeFullscreenVideo();
			
			// 直接重置所有状态
			this.isMaximized = false;
			this.maximizedVideo = null;
			this.maximizedVideoType = '';
			this.maximizedVideoId = '';
			
			// 强制更新DOM
			this.$forceUpdate();
			
			console.log('✅ 强制关闭完成');
		},
		
		// 自动关闭设置对话框（让用户看不见）
		autoCloseSettingDialog() {
			console.log('=== 自动关闭设置对话框 ===');
			
			// 方法1：查找并点击设置对话框的取消按钮
			const settingDialogs = document.querySelectorAll('.setting');
			settingDialogs.forEach((dialog, index) => {
				console.log(`找到设置对话框 ${index + 1}，尝试自动关闭`);
				
				// 查找取消按钮
				const cancelBtn = Array.from(dialog.querySelectorAll('.el-button')).find(btn => 
					btn.textContent.trim() === '取消'
				);
				
				if (cancelBtn) {
					console.log(`自动点击取消按钮，关闭设置对话框 ${index + 1}`);
					cancelBtn.click();
				} else {
					console.log(`未找到取消按钮，尝试查找关闭按钮`);
					// 如果没有取消按钮，尝试关闭按钮
					const closeBtn = dialog.querySelector('.el-dialog__close');
					if (closeBtn) {
						console.log(`自动点击关闭按钮，关闭设置对话框 ${index + 1}`);
						closeBtn.click();
					}
				}
			});
			
			// 方法2：查找包含"确定"/"取消"按钮的对话框
			const allDialogs = document.querySelectorAll('.el-dialog');
			allDialogs.forEach((dialog, index) => {
				const buttons = dialog.querySelectorAll('.el-button');
				buttons.forEach(btn => {
					const btnText = btn.textContent.trim();
					if (btnText === '取消') {
						console.log(`找到取消按钮，自动点击关闭对话框 ${index + 1}`);
						btn.click();
					}
				});
			});
			
			// 方法3：强制隐藏所有设置对话框
			setTimeout(() => {
				const remainingDialogs = document.querySelectorAll('.setting');
				remainingDialogs.forEach((dialog, index) => {
					console.log(`强制隐藏剩余设置对话框 ${index + 1}`);
					dialog.style.display = 'none';
					dialog.style.visibility = 'hidden';
					dialog.style.opacity = '0';
				});
			}, 100);
			
			console.log('✅ 设置对话框自动关闭完成');
		},

	},
	
			// 监听最大化状态变化
		watch: {
			isMaximized(newVal) {
				if (newVal && this.maximizedVideo) {
					// 对话框打开后，等待DOM更新完成再设置视频流
					this.$nextTick(() => {
						setTimeout(() => {
							this.setMaximizedVideoStream();
						}, 100); // 延迟100ms确保对话框完全打开
					});
				}
			}
		},
	
	mounted() {
		console.log('RtcGroupVideo组件已挂载');
		
		// 添加键盘事件监听
		document.addEventListener('keydown', this.handleKeydown);
	},
	
	beforeDestroy() {
		console.log('=== 组件即将销毁，开始清理 ===');
		
		// 移除键盘事件监听
		document.removeEventListener('keydown', this.handleKeydown);
		
		// 关闭全屏视频（如果存在）
		this.closeFullscreenVideo();
		
		// 彻底清理所有资源
		this.cleanup();
		
		console.log('✅ 组件销毁清理完成');
	},
	

}
</script>

<style lang="scss" scoped>
.rtc-group-video-title {
	display: flex;
	align-items: center;
	gap: 18px;
	line-height: 24px;
	font-size: 18px;
	font-weight: 500;
	color: #1f2933;
}

.call-duration {
	min-width: 72px;
	font-size: 20px;
	font-weight: 700;
	font-family: Arial, Helvetica, sans-serif;
	font-variant-numeric: tabular-nums;
	color: #111;
	letter-spacing: 0;
}

.rtc-group-video {
	height: 720px;
	display: flex;
	flex-direction: column;
	overflow: hidden;  /* 防止内容溢出 */
	
	.video-container {
		flex: 1;
		min-height: 0;  /* 关键：防止flex项目溢出 */
		padding: 10px;
		overflow: hidden;  /* 隐藏溢出内容 */
		
		.video-grid {
			display: grid;
			grid-auto-rows: 1fr;  /* 确保行高均等 */
			gap: 10px;
			height: 100%;
			overflow: hidden;  /* 防止内容溢出 */
			
			&.grid-layout-1 {
				grid-template-columns: minmax(0, 1fr);
			}

			&.grid-layout-2 {
				grid-template-columns: repeat(2, minmax(0, 1fr));
			}

			&.grid-layout-3 {
				grid-template-columns: repeat(3, minmax(0, 1fr));
			}

			&.grid-layout-4 {
				grid-template-columns: repeat(4, minmax(0, 1fr));
			}
			
			.video-item {
				position: relative;
				border-radius: 8px;
				overflow: hidden;
				background: #000;
				min-height: 0;  /* 防止内容撑开 */
				min-width: 0;
				
				video {
					width: 100%;
					height: 100%;
					object-fit: cover;
					background: #000;
				}
				
				.video-info {
					position: absolute;
					bottom: 10px;
					left: 10px;
					background: rgba(0, 0, 0, 0.7);
					color: white;
					padding: 5px 10px;
					border-radius: 4px;
					font-size: 12px;
					
					.peer-name {
						margin-right: 5px;
					}
					
					.camera-status, .mic-status {
						margin-left: 3px;
					}
				}
				
				&.local-video {
					border: 2px solid #409EFF;
				}
				
				// 最大化按钮样式
				.maximize-btn {
					position: absolute;
					top: 10px;
					right: 10px;
					width: 32px;
					height: 32px;
					background: rgba(0, 0, 0, 0.7);
					color: white;
					border-radius: 50%;
					display: flex;
					align-items: center;
					justify-content: center;
					cursor: pointer;
					transition: all 0.3s ease;
					z-index: 10;
					
					&:hover {
						background: rgba(0, 0, 0, 0.9);
						transform: scale(1.1);
					}
					
					svg {
						width: 16px;
						height: 16px;
					}
				}

				&.left-user {
					background: #f0f0f0;
					border: 1px solid #eee;
					padding: 10px;
					text-align: center;
					font-size: 14px;
					color: #555;

					.left-user-placeholder {
						display: flex;
						align-items: center;
						gap: 10px;
					}

					.left-user-avatar {
						width: 40px;
						height: 40px;
						border-radius: 50%;
						overflow: hidden;
						background: #eee;
						display: flex;
						align-items: center;
						justify-content: center;
						flex-shrink: 0;

					}

					.left-user-info {
						display: flex;
						flex-direction: column;
						justify-content: center;
					}

					.left-user-name {
						font-weight: bold;
						color: #333;
					}

					.left-user-status {
						font-size: 12px;
						color: #999;
					}
				}

				&.rejected-user {
					background: #ffe0e0;
					border: 1px solid #ffb0b0;
					padding: 10px;
					text-align: center;
					font-size: 14px;
					color: #993333;

					.rejected-user-placeholder {
						display: flex;
						align-items: center;
						gap: 10px;
					}

					.rejected-user-avatar {
						width: 40px;
						height: 40px;
						border-radius: 50%;
						overflow: hidden;
						background: #ffe0e0;
						display: flex;
						align-items: center;
						justify-content: center;
						flex-shrink: 0;

					}

					.rejected-user-info {
						display: flex;
						flex-direction: column;
						justify-content: center;
					}

					.rejected-user-name {
						font-weight: bold;
						color: #993333;
					}

					.rejected-user-status {
						font-size: 12px;
						color: #993333;
					}
				}

				&.timeout-user {
					background: #fff3e0;
					border: 1px solid #ffcc80;
					padding: 10px;
					text-align: center;
					font-size: 14px;
					color: #e65100;

					.timeout-user-placeholder {
						display: flex;
						align-items: center;
						gap: 10px;
					}

					.timeout-user-avatar {
						width: 40px;
						height: 40px;
						border-radius: 50%;
						overflow: hidden;
						background: #fff3e0;
						display: flex;
						align-items: center;
						justify-content: center;
						flex-shrink: 0;

					}

					.timeout-user-info {
						display: flex;
						flex-direction: column;
						justify-content: center;
					}

					.timeout-user-name {
						font-weight: bold;
						color: #e65100;
					}

					.timeout-user-status {
						font-size: 12px;
						color: #e65100;
					}
				}
			}
		}
	}
	
	.control-panel {
		padding: 15px;
		background: #f5f5f5;
		border-top: 1px solid #ddd;
		flex-shrink: 0;  /* 防止控制面板被压缩 */
		
		.control-buttons {
			display: flex;
			justify-content: center;
			gap: 10px;
			margin-bottom: 10px;
			
			/* 统一控制按钮主色：与聊天窗口发送按钮保持一致的浅蓝色 */
			.el-button--primary {
				background-color: #d4def5;
				border-color: #d4def5;
				color: #111827;
				box-shadow: none;
			}
			
			.el-button--primary:hover,
			.el-button--primary:focus {
				background-color: #c3cfef;
				border-color: #c3cfef;
				color: #111827;
			}
		}
		
		.status-info {
			text-align: center;
			font-size: 14px;
			color: #666;
			
			span {
				margin: 0 10px;
			}
		}
	}
}


</style>
